import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormState } from 'react-final-form';
import { useIntl } from 'react-intl';
import { useTheme } from 'styled-components';

import { Input, InputPassword, Tooltip } from '@xsolla/ui-core';
import { AlertExclamationMini } from '@xsolla/ui-icons';

import { FlexParentBlock } from '../../../../styled';
import { getRandomString } from '../../../../utils';
import { CheckmarkMini } from '../../../ui';
import { ExtendedProjectConfiguration, Props } from '../types';
import {
  PASSWORD,
  specialCharacters,
  USERNAME,
  ValidationFieldName,
  validationTypes
} from '../utils';
import { IconWrapper, Text, TooltipWrapper, Wrapper } from './style';
import { ValidationState } from './types';

const TestValidation = ({ name }: Props): JSX.Element => {
  const intl = useIntl();
  const theme = useTheme();
  const { values } = useFormState<ExtendedProjectConfiguration>();
  const [showTooltip, setShowTooltip] = useState<boolean>(false);
  const [validation, setValidation] = useState<ValidationState>({
    value: '',
    errors: []
  });

  useEffect(() => {
    setValidation({ value: '', errors: [] });
  }, [values]);

  const hasValidation = useMemo(() => {
    return Object.values(values.fields_validation).some((value) => value);
  }, [values]);

  const testValidate = useCallback(
    (el) => {
      if (values?.fields_validation) {
        const { value } = el.target;
        const errors: string[] = [];

        Object.entries(values.fields_validation)
          .filter(
            ([key, field]) =>
              !!field &&
              key !== PASSWORD.SPEC_CHARACTERS &&
              key !== PASSWORD.MIN_CHARACTERS &&
              key !== USERNAME.MAX_CHARACTERS
          )
          .forEach(([key]) => {
            const regExp = new RegExp(validationTypes[key].regExp);

            if (!regExp.test(value)) {
              errors.push(key);
            }
          });

        if (values.fields_validation.min_characters) {
          const regExp = new RegExp(
            validationTypes.min_characters.regExp.replace(
              'number',
              values.validation_min_length.toString()
            )
          );

          if (!regExp.test(value)) {
            errors.push(PASSWORD.MIN_CHARACTERS);
          }
        }

        if (values.fields_validation.max_characters) {
          const regExp = new RegExp(
            validationTypes.max_characters.regExp.replace(
              'number',
              values.validation_max_length?.toString() || '30'
            )
          );

          if (!regExp.test(value)) {
            errors.push(USERNAME.MAX_CHARACTERS);
          }
        }

        if (values.fields_validation.spec_characters) {
          let combineRegExp = '';
          values.validation_spec_symbol?.forEach((symbol, index) => {
            if (index === 0) {
              combineRegExp += specialCharacters[symbol];
            } else {
              combineRegExp += `|${specialCharacters[symbol]}`;
            }
          });

          const regExp = new RegExp(combineRegExp.replaceAll(new RegExp('[/]|[/]', 'g'), ''));

          if (!regExp.test(value)) {
            errors.push(PASSWORD.SPEC_CHARACTERS);
          }
        }

        setValidation({
          value,
          errors: value === '' ? [] : errors
        });
      } else {
        setValidation({
          value: el.target.value,
          errors: []
        });
      }
    },
    [values]
  );

  const getErrorText = (item: string): string => {
    if (item === PASSWORD.SPEC_CHARACTERS) {
      let resString = intl.formatMessage({
        id: 'configuration-page.validation-rule.spec_characters'
      });

      values.validation_spec_symbol?.forEach((value, index) => {
        if (index === 0) {
          resString += `: ${value}`;
        } else {
          resString += value;
        }
      });

      return resString;
    }

    if (item === USERNAME.MAX_CHARACTERS) {
      return intl.formatMessage(
        { id: `configuration-page.validation-rule.max_characters` },
        { number: values.validation_max_length }
      );
    }

    if (item === PASSWORD.MIN_CHARACTERS) {
      return intl.formatMessage(
        { id: `configuration-page.validation-rule.min_characters` },
        { number: values.validation_min_length }
      );
    }

    return intl.formatMessage({
      id: `configuration-page.validation-rule.${item}`
    });
  };

  const renderTooltip = () => {
    return (
      <Wrapper data-test-id={`validation-tooltip__${name}`}>
        {Object.entries(values.fields_validation)
          .filter(([, field]) => !!field)
          .map(([key]) => {
            const hasError = validation.errors.includes(key);
            return (
              <FlexParentBlock key={getRandomString()}>
                <IconWrapper>
                  {hasError ? (
                    <AlertExclamationMini color={theme?.colors.danger} />
                  ) : (
                    <CheckmarkMini isDone={!hasError && validation.value.length !== 0} />
                  )}
                </IconWrapper>
                <Text hasError={hasError || validation.value === ''}>{getErrorText(key)}</Text>
              </FlexParentBlock>
            );
          })}
      </Wrapper>
    );
  };

  return (
    <>
      <Tooltip
        content={renderTooltip()}
        offset={6}
        placement="top-start"
        controlledVisible={showTooltip}>
        <TooltipWrapper />
      </Tooltip>
      {name === ValidationFieldName.PASSWORD_FIELD ? (
        <InputPassword
          name="validation_test"
          id="validation_test"
          value={validation.value}
          disabled={!hasValidation}
          spellCheck={false}
          onFocus={() => setShowTooltip(true)}
          onBlur={() => setShowTooltip(false)}
          onChange={(el) => testValidate(el)}
        />
      ) : (
        <Input
          name="validation_test"
          id="validation_test"
          value={validation.value}
          disabled={!hasValidation}
          spellCheck={false}
          onFocus={() => setShowTooltip(true)}
          onBlur={() => setShowTooltip(false)}
          onChange={(el) => testValidate(el)}
        />
      )}
    </>
  );
};

export default TestValidation;
